import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData, getTablebySql } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 그리드 공통
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface 현재 오픈된 메뉴의 메뉴 정보 (CD_MENU, NM_MENU, CD_MODULE...)
import SnResizeBar from "@shared/components/SnResizeBar";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";

interface Props {
  Menuprops: menuInfo;
}
interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

/** 위치구분(MAS021) 3 = 외주창고. 이 화면에서 입력/수정/삭제를 막는다.
 *  (수정 차단은 sys_formgridcolumn.dc_editcon 에 'fg_loc == ''3''' 조건으로 걸려 있다) */
const FG_LOC_OUTSOURCE = "3";

const SN_MAS_ORG_REG_007 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region Variable
  const { userInfo } = global(); // Login user 정보
  const searchRef = useRef<any>(null);
  //#endregion

  //#region Components useState
  const [isLoading, setIsLoading] = useState(false);

  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });

  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();

        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        setColums({
          grid1: rtn.grid.grid1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region Button Events
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));
  //#endregion

  //#region onSearch
  const onSearch = async () => {
    const modifiedRows = gridRefs.current["grid1"].current.getChange();
    if (modifiedRows.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      const param = {
        id: "sn_mas_org_reg_007_01",
        params: searchRef.current.getFormdata(),
      };

      const data: any = await fnGetDataSet(param);
      setDataSources({
        grid1: data[0] || [],
      });
      gridRefs.current["grid1"].current.resetUpdate();
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };
  //#endregion

  //#region onInsert
  const onInsert = async () => {
    try {
      if (!searchRef.current?.getFormdata()?.cd_biz) {
        showDialog("alter", "사업장을 선택하시기 바랍니다.");
        return;
      }

      const node = gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onBeforeAddRow
  const onBeforeAddRow = (): boolean | Record<string, any> => {
    try {
      const formdata = searchRef.current.getFormdata();
      const updatedRow = {
        cd_firm: userInfo.cd_firm,
        cd_biz: formdata?.cd_biz,
        fg_loc: "1",
        yn_bf: "N",
        yn_mrp: "N",
        yn_faulty: "N",
        yn_use: "Y",
        yn_control: "N",
        yn_fifo: "N",
      };
      return updatedRow;
    } catch (error: any) {
      return false;
    }
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    const selectedRows =
      gridRefs.current["grid1"].current?.getSelectedRows?.() ?? [];
    if (selectedRows.some((row: any) => row.fg_loc === FG_LOC_OUTSOURCE)) {
      await showDialog("alter", "외주창고는 삭제할 수 없습니다.");
      return;
    }
    gridRefs.current["grid1"].current?.deleteSelectedRows();
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return;
      }
      const changesRow = gridRefs.current["grid1"].current.getChange();
      const Inserts = changesRow.insert ?? [];
      if (changesRow.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return;
      }
      for (let row of Inserts) {
        if (row.cd_wh && row.cd_wh.length > 10) {
          await showDialog("alter", "창고가 10자리를 초과하였습니다.");
          return;
        }
      }
      // 위치구분 콤보에서는 외주창고를 걸렀지만(dc_filter), 붙여넣기는 에디터를 거치지 않는다.
      // 신규/수정 양쪽 모두 저장 직전에 다시 확인한다.
      const outsourceRow = [...Inserts, ...(changesRow.update ?? [])].find(
        (row: any) => row.fg_loc === FG_LOC_OUTSOURCE
      );
      if (outsourceRow) {
        await showDialog(
          "alter",
          "위치구분을 외주창고로 지정할 수 없습니다."
        );
        return;
      }
      const SaveData = {
        mas_wh: changesRow,
      };
      // 저장 API 호출
      await fnSave("SN_MAS_ORG_REG_007_SAVE_01", SaveData);

      // 성공적으로 저장된 후 초기화
      gridRefs.current["grid1"].current.resetUpdate();
    } catch (error: any) {}
  };
  //#endregion

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRow,
        },
      },
    }),
    [columns, dataSources]
  );

  //#region UI
  return (
    <div style={{ height: "100%" }}>
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div style={{ height: "calc(90%)" }}>
        <SnGrid
          ref={gridRefs.current["grid1"]} // Grid Name
          columnConfig={grids.grid1.columns} // 컬럼 설정 전달
          rowData={grids.grid1.dataSource} // Datasource
          etcProps={{ rowSelection: "single", title: "창고 LIST" }}
          events={grids.grid1.events}
        />
      </div>
    </div>
  );
  //#endregion
});
export default SN_MAS_ORG_REG_007;
